import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Link } from 'react-router-dom';
import { toast } from 'sonner';
import {
  ArrowUpRight, Database, Download, FileArchive, FileCode, FileImage, FileSpreadsheet, FileText, Loader2,
} from 'lucide-react';
import { botsApi } from '../../services/api';
import { saveFile } from '../../native/files';

const FILE_ICONS = [
  [/^(csv|tsv|xlsx?|ods|numbers)$/, FileSpreadsheet],
  [/^(png|jpe?g|gif|webp|svg|avif|bmp)$/, FileImage],
  [/^(zip|tar|gz|tgz|7z|rar)$/, FileArchive],
  [/^(js|jsx|ts|tsx|py|rb|go|rs|java|kt|c|cc|cpp|h|cs|php|sh|sql|json|ya?ml|toml|xml|html?|css)$/, FileCode],
];
const fileIcon = (ext) => (FILE_ICONS.find(([re]) => re.test(ext)) || [null, FileText])[1];

/** A document the bot wrote: a card that opens it, like an artifact under a Claude answer. */
const DocumentCard = ({ doc }) => {
  const { t } = useTranslation();
  const table = doc.type === 'database';
  const Icon = table ? Database : FileText;
  return (
    <Link to={`/app/documents/${doc.id}`} data-testid="answer-document"
      className="group/out flex items-stretch w-full sm:w-[300px] max-w-full overflow-hidden rounded-2xl bg-bubble-bot hover:bg-accent transition-colors">
      <span className="w-12 shrink-0 flex items-center justify-center bg-brand-soft/60 text-brand-icon">
        <Icon className="w-5 h-5" />
      </span>
      <span className="min-w-0 flex-1 px-3 py-2">
        <span className="block text-[13px] font-medium text-foreground truncate">{doc.title || t('quick_create.document')}</span>
        <span className="block text-[11.5px] text-muted-foreground">{table ? t('projects.doc_types.database') : t('quick_create.document')}</span>
      </span>
      <span className="pr-3 flex items-center text-muted-foreground group-hover/out:text-foreground">
        <ArrowUpRight className="w-4 h-4 transition-transform group-hover/out:-translate-y-0.5 group-hover/out:translate-x-0.5" />
      </span>
    </Link>
  );
};

/**
 * A file the bot made on the computer: a chip that downloads it. One it handed over
 * (fileId) is kept by BusyBee.Day; the others come from the computer while it has them.
 */
const FileCard = ({ file, onDownload }) => {
  const { t } = useTranslation();
  const [busy, setBusy] = useState(false);
  const Icon = fileIcon(file.ext);
  const download = async () => {
    setBusy(true);
    try {
      if (file.fileId) await saveFile((await botsApi.fileBlob(file.fileId)).data, file.name);
      else await onDownload(file.path);
    } catch {
      toast.error(t('bots.desktop.download_failed'));
    } finally { setBusy(false); }
  };
  return (
    <button type="button" onClick={download} disabled={busy} title={file.path} data-testid="answer-file"
      className="group/out flex items-center gap-2.5 w-full sm:w-[300px] max-w-full rounded-2xl bg-bubble-bot p-2 text-left hover:bg-accent transition-colors disabled:opacity-70">
      <span className="w-9 h-9 shrink-0 rounded-lg border border-border/70 bg-background flex items-center justify-center text-foreground/75">
        <Icon className="w-4 h-4" />
      </span>
      <span className="min-w-0 flex-1">
        <span className="block text-[13px] font-medium text-foreground truncate">{file.name}</span>
        <span className="block text-[11px] uppercase tracking-wide text-muted-foreground truncate">
          {file.ext ? `${file.ext} · ` : ''}{t('common.download')}
        </span>
      </span>
      <span className="w-7 h-7 shrink-0 rounded-lg flex items-center justify-center text-muted-foreground group-hover/out:text-foreground">
        {busy ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Download className="w-3.5 h-3.5" />}
      </span>
    </button>
  );
};

/**
 * What a bot's run made, right under its answer: the documents it wrote and the files
 * it saved, so people open or download them from the answer instead of looking for
 * them in the step list. Files show only where they can be downloaded.
 */
const AnswerOutputs = ({ outputs, onDownload }) => {
  const shown = (outputs || []).filter((o) => o.kind === 'document' || (o.kind === 'file' && (o.fileId || onDownload)));
  if (!shown.length) return null;
  return (
    <div className="flex flex-wrap gap-2" data-testid="answer-outputs">
      {shown.map((o) => (o.kind === 'document'
        ? <DocumentCard key={`d-${o.id}`} doc={o} />
        : <FileCard key={`f-${o.path}`} file={o} onDownload={onDownload} />))}
    </div>
  );
};

export default AnswerOutputs;
